'use client';

import { useCallback, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { ConfirmDialog } from '../../components/confirm-dialog';

import { extractDroppedFiles } from './folder-drop';

export type DropZoneProps = {
	onDragEnter: (e: React.DragEvent) => void;
	onDragOver: (e: React.DragEvent) => void;
	onDragLeave: (e: React.DragEvent) => void;
	onDrop: (e: React.DragEvent) => void;
};

export type FileDropOptions = {
	/**
	 * Walk dropped directories and confirm before attaching their contents.
	 * Off by default so surfaces that expect a single file (avatar picker) or
	 * that never wanted a dialog (marketing composers) keep their original
	 * fully-synchronous behaviour.
	 */
	acceptFolders?: boolean;
	/** Ceiling on how many files one folder drop may contribute. */
	maxFiles?: number;
};

type PendingFolderDrop = {
	files: File[];
	folderNames: string[];
	/** True when the set was cut down by `maxFiles` or the traversal hard cap. */
	capped: boolean;
};

const quoteFolders = (names: string[]) =>
	names.map((name) => `"${name}"`).join(', ');

const fileCountLabel = (count: number) =>
	count === 1 ? '1 file' : `${count} files`;

/**
 * Encapsulates HTML5 drag-and-drop for file drops on a single element.
 * Uses a ref counter to correctly handle nested child elements.
 *
 * With `acceptFolders`, a dropped directory is walked (see `folder-drop`) and
 * the flattened file list is offered behind a confirmation dialog — the caller
 * renders the returned `folderDropDialog` node somewhere in its tree. Plain
 * file drops still go straight through, so nothing changes for them.
 *
 * For the global "is the user dragging files into the window" signal used
 * by visual affordances like ComposerDropGlow, see useFileDragState.
 */
export function useFileDrop(
	onFiles: (files: FileList | File[]) => void,
	options?: FileDropOptions,
): {
	isDragging: boolean;
	dropZoneProps: DropZoneProps;
	folderDropDialog: ReactNode;
} {
	const [isDragging, setIsDragging] = useState(false);
	const [pending, setPending] = useState<PendingFolderDrop | null>(null);
	const counterRef = useRef(0);

	const acceptFolders = options?.acceptFolders ?? false;
	const maxFiles = options?.maxFiles;

	const hasFiles = (e: React.DragEvent) =>
		e.dataTransfer.types.includes('Files');

	const onDragEnter = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		if (!hasFiles(e)) return;
		counterRef.current += 1;
		if (counterRef.current === 1) setIsDragging(true);
	}, []);

	const onDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = 'copy';
	}, []);

	const onDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		counterRef.current -= 1;
		if (counterRef.current <= 0) {
			counterRef.current = 0;
			setIsDragging(false);
		}
	}, []);

	const onDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			counterRef.current = 0;
			setIsDragging(false);

			if (!acceptFolders) {
				if (e.dataTransfer.files.length > 0) onFiles(e.dataTransfer.files);
				return;
			}

			// `extractDroppedFiles` snapshots `dataTransfer.items` synchronously
			// before its first await, so handing the object across this async hop
			// is safe even though the browser clears the list on handler exit.
			const dataTransfer = e.dataTransfer;
			void extractDroppedFiles(dataTransfer).then((payload) => {
				if (!payload.hadFolder) {
					if (payload.files.length > 0) {
						onFiles(payload.files.map((f) => f.file));
					}
					return;
				}

				const totalFound = payload.files.length;
				if (totalFound === 0) {
					toast.info(
						payload.folderNames.length > 1
							? 'No files found in the dropped folders'
							: 'No files found in the dropped folder',
					);
					return;
				}

				const limited =
					maxFiles !== undefined && totalFound > maxFiles
						? payload.files.slice(0, Math.max(maxFiles, 0))
						: payload.files;

				if (limited.length === 0) {
					toast.error('Attachment limit reached - remove some attachments first');
					return;
				}

				setPending({
					files: limited.map((f) => f.file),
					folderNames: payload.folderNames,
					capped: payload.truncated || limited.length < totalFound,
				});
			});
		},
		[acceptFolders, maxFiles, onFiles],
	);

	const handleCancel = useCallback(() => setPending(null), []);

	const handleConfirm = useCallback(() => {
		if (!pending) return;
		const { files, folderNames } = pending;
		setPending(null);
		onFiles(files);
		toast.success(
			folderNames.length > 1
				? `Files added from ${quoteFolders(folderNames)}`
				: `${fileCountLabel(files.length)} added from ${quoteFolders(folderNames)}`,
		);
	}, [onFiles, pending]);

	const folderDropDialog = pending ? (
		<ConfirmDialog
			isOpen
			onClose={handleCancel}
			title="Add folder contents?"
			description={
				`Add ${fileCountLabel(pending.files.length)} from ${quoteFolders(pending.folderNames)}? ` +
				'Folder structure is not kept - files are attached individually.' +
				(pending.capped
					? ` Only the first ${fileCountLabel(pending.files.length)} will be added.`
					: '')
			}
			confirmLabel={`Add ${fileCountLabel(pending.files.length)}`}
			confirmVariant="solid"
			onConfirm={handleConfirm}
		/>
	) : null;

	return {
		isDragging,
		dropZoneProps: { onDragEnter, onDragOver, onDragLeave, onDrop },
		folderDropDialog,
	};
}
